import { RegionShared } from "@1games-ru/region-shared";
import { useMemo } from "react";
import clsx from "clsx";
import { MAX_RPM } from "@/core/constants/vehicleLimits";
import { EngineIcon, FuelIcon, KeyIcon, SeatbeltIcon, TemperatureIcon } from "@/shared/icons";
import splitIntoIntervals from "@/domains/hud/helpers/splitIntoIntervals";

type Props = {
  className?: string;
} & typeof RegionShared.Cef.Requests.SetVehicleStateRequest;

export default function HudSpeedometerMinimal({
  className,
  fuel,
  fuelCapacity,
  isEngine,
  isLocked,
  isSeatBelt,
  oilTemperature,
  rpm,
  speed,
}: Props) {
  const fuelChunks = useMemo(() => splitIntoIntervals(fuelCapacity, 6), [fuelCapacity]);

  return (
    <div className={clsx("w-[21rem]", className)}>
      <div className="flex items-start justify-between gap-5">
        <div className="flex items-end gap-1">
          <span className="w-[8rem] font-bold text-[64px] text-white leading-[0.75] text-right tabular-nums">
            {speed}
          </span>
          <span className="text-[--orange] text-14 font-medium leading-[0.75]">km/h</span>
        </div>
        <div className="flex items-start justify-between gap-3">
          <div className="flex flex-col items-center gap-1">
            <span
              className={clsx(
                "font-bold text-12 leading-[0.75]",
                isEngine ? "text-white" : "text-white/50",
              )}
            >
              B
            </span>
            <EngineIcon size={36} className={isEngine ? "text-white" : "text-white/50"} />
          </div>
          <div className="flex flex-col items-center gap-1">
            <span
              className={clsx(
                "font-bold text-12 leading-[0.75]",
                isSeatBelt ? "text-white" : "text-white/50",
              )}
            >
              J
            </span>
            <SeatbeltIcon size={36} className={isSeatBelt ? "text-white" : "text-white/50"} />
          </div>
          <div className="flex flex-col items-center gap-1">
            <span
              className={clsx(
                "font-bold text-12 leading-[0.75]",
                isLocked ? "text-white" : "text-white/50",
              )}
            >
              L
            </span>
            <KeyIcon size={36} className={isLocked ? "text-white" : "text-white/50"} />
          </div>
        </div>
      </div>

      <div className="relative h-[11px] bg-[#8F8F8F]/50 mt-6 overflow-hidden">
        <div
          className="absolute h-full w-full bg-[#FFC147]"
          style={{ right: `${100 - (rpm / MAX_RPM) * 100}%` }}
        />
      </div>
      <div className="flex items-center gap-3 mt-4">
        <div className="flex items-center gap-1">
          <FuelIcon size={20} />
          <span className="text-white text-18 font-medium leading-none tabular-nums">
            {fuelCapacity}
          </span>
        </div>
        <div className="flex items-center grow gap-1">
          {fuelChunks.map((item, index) => {
            const newRangeChunk = [0, item[1] - item[0]];
            const newFuel = fuel < item[0] ? 0 : fuel - item[0];
            const percent = (newFuel / newRangeChunk[1]) * 100 || 0;

            return (
              <div
                key={`${index + index}`}
                className="h-2 grow bg-[#D9D9D9]/50"
                style={{
                  background: `linear-gradient(90deg, #FFC147 0%, #FFC147 ${percent}%, rgba(255,255,255,0.2) ${percent}%)`,
                }}
              />
            );
          })}
        </div>
        <div className="flex items-center gap-1 ml-auto">
          <TemperatureIcon size={20} />
          <span className="text-white text-18 font-medium leading-none tabular-nums">
            {oilTemperature}&#176;
          </span>
        </div>
      </div>
    </div>
  );
}
